import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { DAYS_OF_WEEK, type TimeZoneSetting } from '@/services/targeting/targeting'
import { FieldLabel } from '../FieldLabel'

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)

export interface DayPartingRow {
  daysOfWeek?: string[]
  hoursOfDay?: number[]
  timeZoneSetting?: TimeZoneSetting
}

export interface DayPartingTargetEditorProps {
  value: DayPartingRow[]
  onChange: (value: DayPartingRow[]) => void
}

/**
 * Day parting. A row delivers on every combination of the days and the hours it
 * names, so a row needs at least one of each to match anything; further rows
 * add further windows.
 */
export function DayPartingTargetEditor({ value, onChange }: DayPartingTargetEditorProps) {
  const { t } = useTranslation()

  const update = (index: number, patch: DayPartingRow) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  const toggle = <T,>(list: T[] | undefined, item: T): T[] => {
    const current = list ?? []
    return current.includes(item) ? current.filter((v) => v !== item) : [...current, item]
  }

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 && (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {t('targeting.dayParting.empty')}
        </p>
      )}

      {value.map((row, index) => {
        const incomplete = !row.daysOfWeek?.length || !row.hoursOfDay?.length
        return (
          <div key={index} className="flex flex-col gap-3 rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                {t('targeting.dayParting.window', { number: index + 1 })}
              </span>
              <div className="flex items-center gap-2">
                <Select
                  value={row.timeZoneSetting ?? 'SYSTEM'}
                  onValueChange={(v) => update(index, { timeZoneSetting: v as TimeZoneSetting })}
                >
                  <SelectTrigger size="sm" className="w-44">
                    <SelectValue>
                      {t(`targeting.timeZone.${row.timeZoneSetting ?? 'SYSTEM'}`)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SYSTEM">{t('targeting.timeZone.SYSTEM')}</SelectItem>
                    <SelectItem value="USER">{t('targeting.timeZone.USER')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.remove')}
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel className="text-xs font-medium">
                {t('targeting.dayParting.days')}
              </FieldLabel>
              <div className="flex flex-wrap gap-1">
                {DAYS_OF_WEEK.map((day) => (
                  <Button
                    key={day}
                    type="button"
                    size="sm"
                    variant={row.daysOfWeek?.includes(day) ? 'default' : 'outline'}
                    className="h-7 px-2 text-xs"
                    onClick={() => update(index, { daysOfWeek: toggle(row.daysOfWeek, day) })}
                  >
                    {t(`targeting.days.${day}`)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel className="text-xs font-medium">
                {t('targeting.dayParting.hours')}
              </FieldLabel>
              <div className="grid grid-cols-12 gap-1">
                {HOURS.map((hour) => (
                  <Button
                    key={hour}
                    type="button"
                    size="sm"
                    variant={row.hoursOfDay?.includes(hour) ? 'default' : 'outline'}
                    className="h-7 px-0 text-xs tabular-nums"
                    onClick={() => update(index, { hoursOfDay: toggle(row.hoursOfDay, hour) })}
                  >
                    {String(hour).padStart(2, '0')}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => update(index, { hoursOfDay: HOURS })}
                >
                  {t('targeting.dayParting.allHours')}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => update(index, { hoursOfDay: [] })}
                >
                  {t('common.clear')}
                </Button>
              </div>
            </div>

            {incomplete && (
              <p className={cn('text-xs text-(--ad-badge-amber)')}>
                {t('targeting.dayParting.incomplete')}
              </p>
            )}
          </div>
        )
      })}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={() => onChange([...value, { timeZoneSetting: 'SYSTEM' }])}
      >
        <Plus className="size-4" />
        {t('targeting.dayParting.addWindow')}
      </Button>
    </div>
  )
}
